import {
  ActivePartiesResponseSchema,
  ApiErrorCodeSchema,
  ApiErrorSchema,
  AuthStatusSchema,
  CreatePartyResponseSchema,
  DjViewSchema,
  JoinPartyResponseSchema,
  ListeningHistorySchema,
  MyPartyResponseSchema,
  PastPartiesResponseSchema,
  PartyExportSchema,
  PartyViewSchema,
  PartyWrappedSchema,
  ResolvePartyResponseSchema,
  SearchResultsSchema,
  SuggestAckSchema,
  SuggesterViewSchema,
  VerifyPasskeyResponseSchema,
  type ActivePartiesResponse,
  type AuthStatus,
  type ChannelId,
  type CreatePartyRequest,
  type CreatePartyResponse,
  type DjView,
  type JoinChannelRequest,
  type JoinPartyResponse,
  type ListeningHistory,
  type MyPartyResponse,
  type PastPartiesResponse,
  type PartyExport,
  type PartySlug,
  type PartyView,
  type PartyWrapped,
  type ResolvePartyResponse,
  type SearchResults,
  type SuggestAck,
  type SuggesterToken,
  type SuggesterView,
  type TakeoverChannelRequest,
  type TrackId,
  type VerifyPasskeyResponse,
} from '@mixwire/contracts';
import { z } from 'zod';

export const API_BASE = '/api';

const ApiFailureSchema = z
  .strictObject({
    status: z.number().int(),
    code: z.union([ApiErrorCodeSchema, z.literal('network')]),
    message: z.string(),
    issues: z
      .array(z.strictObject({ path: z.string(), message: z.string() }).readonly())
      .readonly(),
  })
  .readonly();

export type ApiFailure = z.infer<typeof ApiFailureSchema>;

// `functional/no-classes` rules out an Error subclass, so `cause` carries the structured
// detail instead — it round-trips through a schema, so reads stay type-safe without a cast.
const toError = (failure: ApiFailure): Error => new Error(failure.message, { cause: failure });

export const readApiFailure = (error: unknown): ApiFailure | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const parsed = ApiFailureSchema.safeParse(error.cause);
  return parsed.success ? parsed.data : undefined;
};

/** A non-2xx response is not guaranteed to carry JSON at all. */
const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
};

const parseFailure = async (response: Response): Promise<ApiFailure> => {
  const parsed = ApiErrorSchema.safeParse(await readJson(response));

  if (!parsed.success) {
    return {
      status: response.status,
      code: 'internal',
      message: `request failed with status ${String(response.status)}`,
      issues: [],
    };
  }

  return {
    status: response.status,
    code: parsed.data.error.code,
    message: parsed.data.error.message,
    issues: parsed.data.error.issues ?? [],
  };
};

/** Turns a transport-level failure into the same ApiFailure shape as an HTTP error. */
const fetchOrFail = async (path: string, init: RequestInit): Promise<Response> => {
  try {
    return await fetch(`${API_BASE}${path}`, init);
  } catch (error: unknown) {
    throw toError({
      status: 0,
      code: 'network',
      message: error instanceof Error ? error.message : 'the network request failed',
      issues: [],
    });
  }
};

const send = async <T>(schema: z.ZodType<T>, path: string, init?: RequestInit): Promise<T> => {
  // `new Headers(...)` not a spread — HeadersInit may be an array of pairs, which
  // spreading would turn into numeric keys.
  const headers = new Headers(init?.headers);
  headers.set('accept', 'application/json');

  const response = await fetchOrFail(path, { ...init, headers });

  if (!response.ok) {
    throw toError(await parseFailure(response));
  }

  return schema.parse(await response.json());
};

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

export const fetchAuthStatus = async (): Promise<AuthStatus> => send(AuthStatusSchema, '/auth/me');

export const logout = async (): Promise<AuthStatus> =>
  send(AuthStatusSchema, '/auth/logout', { method: 'POST' });

export const listActiveParties = async (): Promise<ActivePartiesResponse> =>
  send(ActivePartiesResponseSchema, '/parties');

export const listPastParties = async (): Promise<PastPartiesResponse> =>
  send(PastPartiesResponseSchema, '/parties/past');

export const fetchMyParty = async (): Promise<MyPartyResponse> =>
  send(MyPartyResponseSchema, '/parties/mine');

export const resolveParty = async (name: string): Promise<ResolvePartyResponse> =>
  send(ResolvePartyResponseSchema, `/parties/resolve?name=${encodeURIComponent(name)}`);

export const createParty = async (request: CreatePartyRequest): Promise<CreatePartyResponse> =>
  send(CreatePartyResponseSchema, '/parties', jsonInit('POST', request));

export const joinChannel = async (
  slug: PartySlug,
  channelId: ChannelId,
  request: JoinChannelRequest,
): Promise<JoinPartyResponse> =>
  send(
    JoinPartyResponseSchema,
    `/parties/${slug}/channels/${String(channelId)}/join`,
    jsonInit('POST', request),
  );

export const takeoverChannel = async (
  slug: PartySlug,
  channelId: ChannelId,
  request: TakeoverChannelRequest,
): Promise<JoinPartyResponse> =>
  send(
    JoinPartyResponseSchema,
    `/parties/${slug}/channels/${String(channelId)}/takeover`,
    jsonInit('POST', request),
  );

export const fetchPartyView = async (slug: PartySlug): Promise<PartyView> =>
  send(PartyViewSchema, `/parties/${slug}`);

export const fetchPartyWrapped = async (slug: PartySlug): Promise<PartyWrapped> =>
  send(PartyWrappedSchema, `/parties/${slug}/wrapped`);

export const fetchPartyExport = async (slug: PartySlug): Promise<PartyExport> =>
  send(PartyExportSchema, `/parties/${slug}/export`);

/** Steps the signed-in DJ off their own channel, which stops the server polling them. */
export const leaveChannel = async (slug: PartySlug, channelId: ChannelId): Promise<PartyView> =>
  send(PartyViewSchema, `/parties/${slug}/channels/${String(channelId)}/leave`, {
    method: 'POST',
  });

/** Calls the party over for everyone. Every Spotify poll behind it stops. */
export const endParty = async (slug: PartySlug): Promise<PartyView> =>
  send(PartyViewSchema, `/parties/${slug}/end`, { method: 'POST' });

export const verifyPasskey = async (
  slug: PartySlug,
  passkey: string,
): Promise<VerifyPasskeyResponse> =>
  send(
    VerifyPasskeyResponseSchema,
    `/parties/${slug}/passkey/verify`,
    jsonInit('POST', { passkey }),
  );

export const fetchDjView = async (slug: PartySlug, channelId: ChannelId): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}`);

export const searchDjTracks = async (
  slug: PartySlug,
  channelId: ChannelId,
  query: string,
): Promise<SearchResults> =>
  send(
    SearchResultsSchema,
    `/parties/${slug}/dj/${String(channelId)}/search?q=${encodeURIComponent(query)}`,
  );

/** Puts a crowd suggestion on this DJ's own Spotify queue, which takes it off every console. */
export const queueWant = async (
  slug: PartySlug,
  channelId: ChannelId,
  wantId: TrackId,
): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/wants/${wantId}/queue`, {
    method: 'POST',
  });

/** `track` is a Spotify id or any link form — the server accepts either. */
export const queueTrack = async (
  slug: PartySlug,
  channelId: ChannelId,
  track: string,
): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/queue`, jsonInit('POST', { track }));

/**
 * Holds and releases the DJ's own playback. Spotify answers both with no body, so what actually
 * happened is only visible on the next poll.
 */
export const pausePlayback = async (slug: PartySlug, channelId: ChannelId): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/pause`, { method: 'POST' });

export const resumePlayback = async (slug: PartySlug, channelId: ChannelId): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/resume`, { method: 'POST' });

/** Drops whatever is playing right now. Spotify offers no way to remove anything further down. */
export const skipCurrentTrack = async (slug: PartySlug, channelId: ChannelId): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/skip`, { method: 'POST' });

export const dismissWant = async (
  slug: PartySlug,
  channelId: ChannelId,
  wantId: TrackId,
): Promise<DjView> =>
  send(DjViewSchema, `/parties/${slug}/dj/${String(channelId)}/wants/${wantId}/dismiss`, {
    method: 'POST',
  });

/** Whose history this is comes from the session cookie, not from a parameter. */
export const fetchListeningHistory = async (limit: number): Promise<ListeningHistory> =>
  send(ListeningHistorySchema, `/listens?limit=${String(limit)}`);

export const fetchSuggesterView = async (
  slug: PartySlug,
  token: SuggesterToken,
): Promise<SuggesterView> =>
  send(SuggesterViewSchema, `/parties/${slug}/suggester/view`, {
    headers: { authorization: `Bearer ${token}` },
  });

export const searchSpotifyTracks = async (
  slug: PartySlug,
  token: SuggesterToken,
  query: string,
): Promise<SearchResults> =>
  send(SearchResultsSchema, `/parties/${slug}/suggester/search?q=${encodeURIComponent(query)}`, {
    headers: { authorization: `Bearer ${token}` },
  });

/** `track` is a Spotify id or any link form — the server accepts either. */
export const suggestTrack = async (
  slug: PartySlug,
  token: SuggesterToken,
  track: string,
): Promise<SuggestAck> =>
  send(SuggestAckSchema, `/parties/${slug}/suggester/suggestions`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ track }),
  });
